import React, { useEffect } from "react";

import { Popover, Skeleton, Stack } from "@mui/material";
import { useActionHook } from "hooks";
import { useCashflowCubicConfigSelector, useReportsActions, useReportsQueriesSelector } from "store";

import { Cubic } from "./Cubic";
import { Head } from "./Head";
import { Menu } from "./Menu";

const cubicsQuantity = 5;
const skeletons = new Array(cubicsQuantity).fill("");

interface MenuState {
  anchorEl: HTMLElement | null;
  cubicKey: string;
  title: string;
  filter: string;
}

export const KPIs: React.FC = () => {
  const [menuState, setMenuState] = React.useState<MenuState | null>(null);

  const { getCubicAction, setQueriesAction } = useReportsActions();
  const queries = useReportsQueriesSelector();

  const handleCloseMenu = () => setMenuState(null);

  const handleSubmit = (data: { title: string; filter: string }) => {
    if (!menuState) return;

    const updatedCubic = {
      ...queries.cubicCashflow[menuState.cubicKey],
      title: data.title,
      filter: data.filter
    };

    setQueriesAction({
      cubicCashflow: {
        ...queries.cubicCashflow,
        [menuState.cubicKey]: updatedCubic
      }
    });
    handleCloseMenu();
  };

  //mock for cubic data
  const { dateRange, exchangeMethod, currency, filters, use_accounting_date } = queries;
  const cubicSettings = useCashflowCubicConfigSelector();
  const { data: cubicData, action: getCubic, loading: cubicLoading } = useActionHook(getCubicAction);

  useEffect(() => {
    if (Object.keys(cubicSettings).length) {
      getCubic({
        cubics: cubicSettings,
        pageType: "cubicCashflow",
        filters,
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
  }, [cubicSettings, dateRange, exchangeMethod, currency, filters, use_accounting_date]);

  return (
    <Stack sx={{ gap: "8px", p: "16px", borderRadius: "8px", boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)", bgcolor: "#fff" }}>
      <Head />
      <Stack sx={{ flexDirection: "row", gap: "10px", alignItems: "stretch", flexGrow: 1, minHeight: 60 }}>
        {cubicLoading
          ? skeletons.map((_, index) => <Skeleton key={index} variant="rounded" sx={{ height: "100%", flexGrow: 1 }} />)
          : cubicData &&
            Object.entries(cubicData).map(([key, cubic], index) => (
              <Cubic
                title={cubic.title}
                value={cubic.tooltip || cubic.value}
                key={index}
                onMenuClick={(anchorEl) =>
                  setMenuState({
                    anchorEl,
                    cubicKey: key,
                    title: cubic.title,
                    filter: cubic.filter
                  })
                }
              />
            ))}
      </Stack>
      <Popover anchorEl={menuState?.anchorEl} open={Boolean(menuState?.anchorEl)} onClose={handleCloseMenu}>
        {menuState && (
          <Menu
            filter={menuState?.filter}
            title={menuState?.title}
            onCancel={handleCloseMenu}
            onSubmit={handleSubmit}
          />
        )}
      </Popover>
    </Stack>
  );
};
